import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { leesDrempel } from '../drempel';
import { FOCUSRING } from '../focus';
import type { Lezing, OpslagFout } from '../hooks/useInstellingen';
import { useUitgesteld } from '../hooks/useUitgesteld';
import type { Antwoord, WerksoortSleutel } from '../work-quality.api';
import { useVoorlees } from './Toestandsmelding';

/** Aanraakdoelen van 44×44px, onder 768px en op elk apparaat dat met een vinger wordt bediend. */
const AANRAAKDOEL = 'max-md:min-h-[44px] [@media(pointer:coarse)]:min-h-[44px]';

/** De werksoorten in vaste volgorde, met hun zichtbare naam: Vastleggen vóór Controles (M4). */
export const WERKSOORT_VOLGORDE: ReadonlyArray<readonly [WerksoortSleutel, string]> = [
  ['myxxtract', 'qa.eigen.werksoortMyxxtract'],
  ['pca', 'qa.eigen.werksoortPca'],
];

const tekstOfLeeg = (waarde: unknown) => (typeof waarde === 'string' ? waarde : '');

/** Een persoon zoals hij uit een antwoord komt: elk veld kan ontbreken of onleesbaar zijn. */
interface Persoonsvelden {
  naam?: unknown;
  voornaam?: unknown;
  achternaam?: unknown;
}

/** Een lege of onleesbare voornaam telt als geen voornaam: dan sorteert de hele naam. */
const sorteernaam = (persoon: Persoonsvelden) => tekstOfLeeg(persoon.voornaam) || tekstOfLeeg(persoon.naam);

/**
 * Personen alfabetisch op voornaam (of de hele naam als er geen voornaam is), dan op achternaam,
 * hoofdletter- en accentongevoelig (M8). Sorteren is geen rekenen: er komt geen getal uit.
 */
export function opVoornaam<P extends Persoonsvelden>(personen: readonly P[], locale: string): P[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base' });
  return [...personen].sort(
    (a, b) =>
      collator.compare(sorteernaam(a), sorteernaam(b)) ||
      collator.compare(tekstOfLeeg(a.achternaam), tekstOfLeeg(b.achternaam)),
  );
}

/** De naam zoals de dienst hem stuurt, of "Onbekende medewerker" als er geen staat. */
export function naamVan(persoon: Persoonsvelden, t: TFunction): string {
  return typeof persoon.naam === 'string' && persoon.naam.trim() !== ''
    ? persoon.naam
    : t('qa.instellingen.onbekendeMedewerker');
}

/** Een effect: wat er in het effectvlak staat, en de zin(nen) voor het Voorleesgebied, zonder namen. */
export interface Effect {
  inhoud: ReactNode;
  samenvatting: string;
}

/** De opslag van één instelling, los van welke het is. */
export interface Opslag {
  opslaan: (waarde: number) => void;
  isBezig: boolean;
  isGelukt: boolean;
  fout: OpslagFout | null;
  /** De waarde uit het laatste opslagantwoord van de dienst. */
  opgeslagen: number | null;
  opgeslagenVersie: number | null;
}

export interface InstellingformulierProps<A> {
  /** Welke instelling: `data-werkkwaliteit-sectie` en het voorvoegsel van de toets-id's. */
  kind: 'drempel' | 'doel';
  kop: string;
  uitleg: string;
  label: string;
  eenheid: string;
  /** De amber melding bij een waarde buiten het bereik. */
  bereikMelding: string;
  /** De storing bij een mislukte opslag (`role="alert"`). */
  misluktTekst: string;
  /** De bevestiging na opslaan, zichtbaar én voorgelezen, met de waarde uit het opslagantwoord. */
  bevestiging: (waarde: number) => string;
  /** De waarde van de dienst. Het veld neemt hem één keer over; daarna is het alleen de geldende waarde. */
  huidigeWaarde: number;
  min: number;
  max: number;
  /** De laatst afgesloten week, of leeg zolang er geen is. */
  week: string;
  /** De wat-als-haak; elk formulier geeft de zijne mee (`useWatAls`, `useWatAlsDoel`). */
  useGevolg: (week: string, waarde: number | null) => Lezing<Antwoord<A>>;
  opslag: Opslag;
  /** Het effect van een gemeten antwoord, voor de waarde waarover het antwoord ging. */
  effect: (data: A, waarde: number) => Effect;
}

/**
 * Eén instelling met eronder wat ze doet — het gedeelde deel van Drempel- en Doelformulier (story 12,
 * S7).
 *
 * **Invoer raakt nooit kwijt.** Het veld neemt de waarde van de dienst één keer over en springt niet
 * terug als de instellingen opnieuw gelezen worden; de enige `focus()` gaat naar het eigen veld.
 *
 * **Eén aankondigingsplek** (`useVoorlees`). Het formulier onthoudt de laatste waarde die het
 * aankondigde en zegt alleen iets over een waarde die iemand typte en die nog niet is aangekondigd —
 * nooit bij het laden, en nooit bij een nieuwe lezing na opslaan, zodat de opslagbevestiging blijft
 * staan.
 *
 * **Het vorige effect blijft staan** zolang het nieuwe onderweg is, samen met de waarde waarover het
 * ging: de zin noemt nooit een andere waarde dan het antwoord.
 */
export function Instellingformulier<A>({
  kind: sectie,
  kop,
  uitleg,
  label,
  eenheid,
  bereikMelding,
  misluktTekst,
  bevestiging,
  huidigeWaarde,
  min,
  max,
  week,
  useGevolg,
  opslag,
  effect,
}: InstellingformulierProps<A>) {
  const { t } = useTranslation();
  const voorlees = useVoorlees();
  const basisId = useId();
  const kopId = `${basisId}-kop`;
  const veldId = `${basisId}-veld`;
  const eenheidId = `${basisId}-eenheid`;
  const foutId = `${basisId}-fout`;
  const geweigerdId = `${basisId}-geweigerd`;
  const hintId = `${basisId}-hint`;

  const veld = useRef<HTMLInputElement>(null);
  const getypt = useRef(false);
  const [invoer, setInvoer] = useState(() => String(huidigeWaarde));
  const [geprobeerd, setGeprobeerd] = useState<number | null>(null);

  const binnenBereik = (tekst: string) => {
    const getal = leesDrempel(tekst);
    return getal !== null && getal >= min && getal <= max ? getal : null;
  };
  const waarde = binnenBereik(invoer);
  const uitgesteld = useUitgesteld(invoer);
  // Een ongeldige waarde in het veld vraagt niets, ook niet over de vorige: er gaat geen wat-als uit.
  const gevraagd = waarde === null ? null : binnenBereik(uitgesteld);
  const gevolg = useGevolg(week, gevraagd);

  const huidig = gevraagd !== null && gevolg.data !== undefined ? { waarde: gevraagd, antwoord: gevolg.data } : null;
  const laatste = useRef(huidig);
  if (huidig !== null) laatste.current = huidig;

  function samenvattingVan(antwoord: Antwoord<A>, over: number): string {
    return antwoord.data === null ? t('qa.instellingen.gevolgNietGemeten') : effect(antwoord.data, over).samenvatting;
  }

  // Voorlezen: alleen een getypte, nog niet aangekondigde waarde, en pas als het uitstel voorbij is —
  // tijdens het typen loopt `gevraagd` nog een waarde achter, en die typte niemand.
  const aangekondigd = useRef<number | null>(null);
  useEffect(() => {
    if (!getypt.current || uitgesteld !== invoer || week === '' || gevraagd === null) return;
    if (aangekondigd.current === gevraagd) return;
    if (gevolg.isError) {
      aangekondigd.current = gevraagd;
      voorlees(t('qa.instellingen.effectMislukt'));
    } else if (gevolg.data !== undefined) {
      aangekondigd.current = gevraagd;
      voorlees(samenvattingVan(gevolg.data, gevraagd));
    }
  });

  // De opslagbevestiging één keer per opgeslagen versie.
  const bevestigdeVersie = useRef<string | null>(null);
  useEffect(() => {
    if (!opslag.isGelukt || opslag.opgeslagen === null) return;
    const sleutel = `${opslag.opgeslagenVersie}/${opslag.opgeslagen}`;
    if (bevestigdeVersie.current === sleutel) return;
    bevestigdeVersie.current = sleutel;
    voorlees(bevestiging(opslag.opgeslagen));
  });

  const geldende = opslag.opgeslagen ?? huidigeWaarde;
  // Ongeldig gaat vóór ongewijzigd: een opgeslagen 150 staat er als ongeldig, niet als "al ingesteld".
  const redenGeenOpslag: 'ongeldig' | 'bezig' | 'ongewijzigd' | null =
    waarde === null ? 'ongeldig' : opslag.isBezig ? 'bezig' : waarde === geldende ? 'ongewijzigd' : null;

  function bewaar() {
    // De poort staat hier en niet op de knop: `aria-disabled` houdt de knop bereikbaar.
    if (redenGeenOpslag === 'ongeldig') {
      veld.current?.focus();
      return;
    }
    if (redenGeenOpslag !== null || waarde === null) return;
    setGeprobeerd(waarde);
    // De bevestiging zegt al wat er geldt; het effect van deze waarde hoeft daarna niet meer.
    aangekondigd.current = waarde;
    opslag.opslaan(waarde);
  }

  const opDezeWaarde = waarde !== null && waarde === geprobeerd;
  const geweigerd = opDezeWaarde && opslag.fout === 'geweigerd';
  const mislukt = opDezeWaarde && opslag.fout === 'mislukt';
  const bevestigd = opslag.isGelukt && opslag.opgeslagen !== null && opslag.opgeslagen === waarde;

  // Een weigering staat naast het veld, buiten elk levend gebied; wie het scherm niet ziet, hoort haar
  // via het Voorleesgebied, één keer per geweigerde poging (een nieuwe poging zet `fout` eerst op null).
  useEffect(() => {
    if (geweigerd) voorlees(t('qa.instellingen.opslaanGeweigerd'));
  }, [geweigerd, voorlees, t]);

  const beschreven = [eenheidId, waarde === null ? foutId : null, geweigerd ? geweigerdId : null].filter(Boolean).join(' ');

  function effectvlak(): ReactNode {
    if (week === '') return <p>{t('qa.instellingen.effectGeenWeek')}</p>;
    if (gevraagd !== null && gevolg.isError) return <p>{t('qa.instellingen.effectMislukt')}</p>;
    const getoond = huidig ?? laatste.current;
    if (getoond === null) return <p className="text-[var(--foreground-muted)]">{t('qa.instellingen.gevolgBezig')}</p>;
    if (getoond.antwoord.data === null) return <p>{t('qa.instellingen.gevolgNietGemeten')}</p>;
    return effect(getoond.antwoord.data, getoond.waarde).inhoud;
  }

  const amber = 'rounded-[10px] bg-[var(--warning-light)] px-3 py-2 text-[13px] text-[var(--warning-text)]';

  return (
    <section
      aria-labelledby={kopId}
      className="flex flex-col gap-3 rounded-xl border border-[var(--border-soft)] bg-[var(--background)] p-5"
      data-testid={`werkkwaliteit-${sectie}`}
      data-werkkwaliteit-sectie={sectie}
    >
      <h2 className="text-[13px] font-semibold text-[var(--foreground-heading)]" id={kopId}>
        {kop}
      </h2>
      <p className="text-[13px] text-[var(--foreground-muted)]">{uitleg}</p>

      {/* Een echt formulier: bij één veld is Enter de gebruikelijke handeling. */}
      <form
        className="flex flex-col gap-3"
        onSubmit={(gebeurtenis) => {
          gebeurtenis.preventDefault();
          bewaar();
        }}
      >
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-medium text-[var(--foreground-heading)]" htmlFor={veldId}>
            {label}
          </label>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <input
              aria-describedby={beschreven}
              aria-invalid={waarde === null}
              className={`w-24 rounded-lg border border-[var(--border-strong)] bg-[var(--background)] px-3 py-1.5 text-[13px] text-[var(--foreground)] ${AANRAAKDOEL} ${FOCUSRING}`}
              data-testid={`werkkwaliteit-${sectie}-invoer`}
              id={veldId}
              inputMode="numeric"
              onChange={(gebeurtenis) => {
                const tekst = gebeurtenis.target.value;
                getypt.current = true;
                // Een nieuwe waarde maakt een eerdere weigering of storing ongedaan; een lege of
                // ongeldige tussenstap maakt de laatst aangekondigde waarde weer aankondigbaar.
                setGeprobeerd(null);
                if (binnenBereik(tekst) === null) aangekondigd.current = null;
                setInvoer(tekst);
              }}
              ref={veld}
              type="text"
              value={invoer}
            />
            <span className="text-[13px] text-[var(--foreground-muted)]" id={eenheidId}>
              {eenheid}
            </span>
            <button
              // `aria-disabled` en niet `disabled`: een uitgeschakelde knop is niet met Tab te bereiken.
              aria-describedby={redenGeenOpslag === 'ongeldig' ? foutId : hintId}
              aria-disabled={redenGeenOpslag !== null}
              className={`rounded-lg bg-[var(--primary)] px-3 py-1.5 text-[13px] font-medium text-white hover:bg-[var(--primary-hover)] aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-[var(--primary)] ${AANRAAKDOEL} ${FOCUSRING}`}
              data-testid={`werkkwaliteit-${sectie}-opslaan`}
              type="submit"
            >
              {opslag.isBezig ? t('qa.instellingen.opslaanBezig') : t('qa.instellingen.opslaan')}
            </button>
            <span className="sr-only" id={hintId}>
              {/* Bij bezig zegt de knop het al zelf; twee keer hoeft niet. */}
              {redenGeenOpslag === 'ongewijzigd' ? t('qa.instellingen.opslaanOngewijzigd') : ''}
            </span>
          </div>
        </div>

        {waarde === null && (
          <p className={amber} data-testid={`werkkwaliteit-${sectie}-ongeldig`} id={foutId}>
            {bereikMelding}
          </p>
        )}
        {geweigerd && (
          <p className={amber} data-testid={`werkkwaliteit-${sectie}-geweigerd`} id={geweigerdId}>
            {t('qa.instellingen.opslaanGeweigerd')}
          </p>
        )}
      </form>

      <div data-testid={`werkkwaliteit-${sectie}-opslagfout`} role="alert">
        {mislukt && (
          <p className="rounded-[10px] bg-[var(--destructive-light)] px-3 py-2 text-[13px] text-[var(--foreground)]">
            {misluktTekst}
          </p>
        )}
      </div>

      {bevestigd && (
        <p
          className="rounded-[10px] bg-[var(--success-light)] px-3 py-2 text-[13px] text-[var(--foreground)]"
          data-testid={`werkkwaliteit-${sectie}-opgeslagen`}
        >
          <span aria-hidden="true" className="material-symbols-outlined mr-1.5 align-[-3px] text-[16px] text-[var(--success-dark)]">
            check_circle
          </span>
          {bevestiging(opslag.opgeslagen!)}
        </p>
      )}

      {/* Bij een ongeldige waarde geen effect, ook niet het vorige: alleen de melding hierboven. */}
      {waarde !== null && (
        <div
          className="flex min-w-0 flex-col gap-2 rounded-[10px] bg-[var(--secondary-light)] px-3 py-2.5 text-[13px] text-[var(--foreground)]"
          data-testid={`werkkwaliteit-${sectie}-gevolg`}
        >
          {effectvlak()}
        </div>
      )}
    </section>
  );
}
